import { use, useCallback } from 'react';
import styled from 'styled-components';
import { Hymn } from '../../../common/src/model.ts';
import Add from '../assets/add.svg?react';
import BackArrow from '../assets/arrow-previous-left-icon.svg?react';
import InfoIcon from '../assets/info-outline.svg?react';
import Remove from '../assets/remove.svg?react';
import ReportIssueIcon from '../assets/tools.svg?react';
import { setSongTranspositionForOrg, useCurrentOrg } from '../data/use_current_org.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { SongOptions, SongOptionsSetting } from '../pages/settings/song_options.tsx';
import { CurrentSongContext } from '../player/current_song.tsx';
import { SequencerContext } from '../player/sequencer.tsx';
import './kebab.css';

const keyModulationHeightThreshold = '400px';

interface Props {
  hymn: Hymn;
  displaySongInfo: () => void;
  displayModal: () => void;
  close: () => void;
  initiallyExpandedSetting?: SongOptionsSetting;
}

export const Kebab = ({ displaySongInfo, displayModal, close, hymn, initiallyExpandedSetting }: Props) => {
  const keys = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const { userKeyTransposition, setUserKeyTransposition } = use(CurrentSongContext);
  // a practice video cannot be transposed (GitHub issue #4041)
  const { videoEnabled } = use(SequencerContext);

  const songKeyIndex = keys.findIndex((key) => key === hymn.key);
  const totalHalfSteps = ((currentOrg?.songTranspositions?.[hymn.slug] ?? 0) + userKeyTransposition) * 2;
  // Use floored modulo so negative transpositions wrap correctly (e.g. Eb - 4 half-steps = B, not clamped to C).
  const keyIndex = songKeyIndex < 0 ? -1 : (((songKeyIndex + totalHalfSteps) % 12) + 12) % 12;

  const modulateKey = useCallback(
    (delta: number) => {
      const newModulationValue = Math.min(6, Math.max(-6, userKeyTransposition + delta));
      setUserKeyTransposition(newModulationValue);
    },
    [setUserKeyTransposition, userKeyTransposition],
  );

  const reset = useCallback(() => {
    modulateKey(-userKeyTransposition);
  }, [modulateKey, userKeyTransposition]);

  const onSetSongKeyForOrganization = useCallback(async () => {
    if (!currentOrg) {
      return;
    }
    const modulationValue = userKeyTransposition + (currentOrg?.songTranspositions?.[hymn.slug] ?? 0);
    await updateOrgInfo(
      setSongTranspositionForOrg({ org: currentOrg, songSlug: hymn.slug, modulationHalfSteps: modulationValue }),
    );
    reset();
  }, [currentOrg, hymn.slug, reset, updateOrgInfo, userKeyTransposition]);

  function renderKeyModulation() {
    const sign = userKeyTransposition > 0 ? '+' : '';
    const modulationValue = `${sign}${userKeyTransposition.toFixed(1)}`;
    const modulationElement =
      userKeyTransposition !== 0 && (isInternalUser || isOrgAdmin) ? (
        <SetModulationButton onClick={onSetSongKeyForOrganization}>Set to {modulationValue}</SetModulationButton>
      ) : (
        <ModulationValue>{modulationValue}</ModulationValue>
      );
    return (
      <KeyModulationWrapper>
        <KeyModulationSetting>
          <SettingLabel>Transpose Key</SettingLabel>
          {modulationElement}
        </KeyModulationSetting>

        <div id="modulationContainer">
          <div className="control" id="left">
            <div onClick={() => modulateKey(-0.5)}>
              <Remove className="addSubtractIcon" />
            </div>
          </div>

          <div id="key">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                fontWeight: 'bold',
              }}
            >
              {keyIndex >= 0 ? keys[keyIndex] : hymn.key}
            </div>
          </div>

          <div className="control" id="right">
            <div onClick={() => modulateKey(0.5)}>
              <Add className="addSubtractIcon" />
            </div>
          </div>
          <button id="resetButton" onClick={reset}>
            Reset
          </button>
        </div>
      </KeyModulationWrapper>
    );
  }

  const onReportIssue = useCallback(() => {
    close();
    displayModal();
  }, [close, displayModal]);

  return (
    <KebabWrapper>
      <div id={kebabCloseButtonId} onClick={close}>
        <BackArrow className="backArrow" />
      </div>
      <ReportIssueIconWrapper onClick={onReportIssue} />
      <InfoButton onClick={displaySongInfo} />
      <PlayerSettings>
        <SongOptions initiallyExpandedSetting={initiallyExpandedSetting} />
        {!videoEnabled && renderKeyModulation()}
      </PlayerSettings>
    </KebabWrapper>
  );
};

export const kebabCloseButtonId = 'kebab-close-button';

const KebabWrapper = styled.div`
  position: relative;
  width: 100vw;
`;

const ModulationValue = styled.div`
  color: var(--color-text-light);
  padding: 2px 0;
`;

const KeyModulationSetting = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px 20px;
`;

const SettingLabel = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

const SetModulationButton = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  display: flex;
  gap: 5px;
  background: transparent;
  padding: 0 5px;
  border-radius: 5px;
  cursor: pointer;
`;

const ReportIssueIconWrapper = styled(ReportIssueIcon)`
  position: absolute;
  top: 10px;
  right: 50px;
  height: 30px;
  width: 30px;
  color: var(--color-text);
  fill: var(--color-text);
  transform: scale(0.75);
  cursor: pointer;
`;

const InfoButton = styled(InfoIcon)`
  position: absolute;
  top: 10px;
  right: 10px;
  height: 30px;
  width: 30px;
  color: var(--color-text);
  cursor: pointer;
`;

const PlayerSettings = styled.div`
  margin-left: 5px;
  margin-top: 50px;
`;

const KeyModulationWrapper = styled.div`
  @media (max-height: ${keyModulationHeightThreshold}) {
    display: none;
  }
`;
